import React, { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import TextStyles from "../../components/Elements/TextStyles/TextStyles";
import { ReactComponent as Back } from "../../assets/svgs/back-button.svg";
import { Button, Spin, Skeleton, message } from "antd";
import { API_URLS } from "../../api/apiUrls";
import { getApi } from "../../api/api_instance";
import { QuestionForm } from "../../pages/Organization/components/DynamicForm/ViewForm/QuestionForm";

const scrollableContainer = {
  maxHeight: "400px",
  overflowY: "auto",
  padding: "10px",
  borderRadius: "8px",
};

const ViewForm = () => {
  const [formData, setFormData] = useState({});
  const [formTitle, setFormTitle] = useState("");
  const [sections, setSections] = useState([]);
  const [selectedSection, setSelectedSection] = useState(null);
  const [selectedSubsectionId, setSelectedSubsectionId] = useState(null);
  const [selectedSubsectionIndex, setSelectedSubsectionIndex] = useState(0);
  const [questions, setQuestions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [questionsLoading, setQuestionsLoading] = useState(false);

  const navigate = useNavigate();
  const { formId } = useParams();

  useEffect(() => {
    const fetchFormDetails = async () => {
      try {
        setLoading(true);
        const formResponse = await getApi({
          apiUrl: API_URLS.GET_STANDARD_FORM_BY_ID(formId),
        });
        if (formResponse?.success && formResponse.data) {
          setFormData(formResponse.data);
          setFormTitle(formResponse.data.title || "Untitled Form");
        } else {
          message.error("Failed to fetch form title");
        }

        const sectionResponse = await getApi({
          apiUrl: API_URLS.GET_SECTION_SUBSECTION(formId),
        });
        if (
          sectionResponse?.success &&
          Array.isArray(sectionResponse.data) &&
          sectionResponse.data.length
        ) {
          setSections(sectionResponse.data);
          setSelectedSection(sectionResponse.data[0]);
          const firstSubsectionId =
            sectionResponse.data[0]?.subsection?.[0]?.id;
          if (firstSubsectionId) {
            setSelectedSubsectionId(firstSubsectionId);
          }
          setSelectedSubsectionIndex(0);
        } else {
          message.error("Failed to fetch sections and subsections");
        }
      } catch (error) {
        console.error("Error fetching form data:", error);
        message.error("Error fetching form details");
      } finally {
        setLoading(false);
      }
    };
    fetchFormDetails();
  }, [formId]);

  useEffect(() => {
    const fetchQuestions = async () => {
      if (selectedSubsectionId) {
        try {
          setQuestionsLoading(true);
          const response = await getApi({
            apiUrl: API_URLS.QUESTIONS_SUBSECTION_BY_ID(selectedSubsectionId),
          });
          if (response.success && response.data.length > 0) {
            const allQuestions = response.data.flatMap(
              (item) => item.questions || [],
            );
            setQuestions(allQuestions);
          } else {
            setQuestions([]);
          }
        } catch (err) {
          message.error("Error fetching questions");
        } finally {
          setQuestionsLoading(false);
        }
      }
    };
    fetchQuestions();
  }, [selectedSubsectionId]);

  const handleSectionClick = (sectionItem) => {
    setSelectedSection(sectionItem);
    setSelectedSubsectionId(null);
    setSelectedSubsectionIndex(0);
    setQuestions([]);
  };

  const handleSubsectionClick = (subsectionId, index) => {
    setSelectedSubsectionId(subsectionId);
    setSelectedSubsectionIndex(index);
  };

  const handleNext = () => {
    if (
      selectedSection?.subsection &&
      selectedSubsectionIndex < selectedSection.subsection.length - 1
    ) {
      setSelectedSubsectionIndex((prev) => prev + 1);
      setSelectedSubsectionId(
        selectedSection.subsection[selectedSubsectionIndex + 1]?.id,
      );
    }
  };

  const handlePrevious = () => {
    if (selectedSubsectionIndex > 0) {
      setSelectedSubsectionIndex((prev) => prev - 1);
      setSelectedSubsectionId(
        selectedSection.subsection[selectedSubsectionIndex - 1]?.id,
      );
    }
  };

  return (
    <div className="h-full p-10 bg-white">
      <div className="flex justify-between pb-2">
        <div className="flex items-center">
          <Back className="cursor-pointer" onClick={() => navigate(-1)} />
          <h3 className="text-[#0087A3] text-xl">{formTitle}</h3>
        </div>
      </div>

      <div className="flex flex-col">
        <div className="border border-[#3BA2B959] rounded-[10px] pt-5 shadow-[0_0_16px_0_#3BA2B91F] mb-6">
          {sections.map((sectionItem) => (
            <button
              key={sectionItem.section._id}
              onClick={() => handleSectionClick(sectionItem)}
              className={`pl-20 py-2 bg-transparent cursor-pointer ${sectionItem === selectedSection ? "text-[#0087A3]" : "text-black"}`}
            >
              {sectionItem.section.name}
            </button>
          ))}
          <hr className="border-t-[1px] border-[#3BA2B959] shadow-[0_0_16px_0_#3BA2B91F]" />
          <div className="flex flex-wrap gap-5 pt-5 pb-5 mb-5 pl-16 text-[11px] font-normal border border-[#3BA2B91C] shadow-[0_0_25.2px_0_#3BA2B91F]">
            {Array.isArray(selectedSection?.subsection) &&
            selectedSection.subsection.length
              ? selectedSection.subsection.map((sub, index) => (
                  <button
                    key={sub._id}
                    onClick={() => handleSubsectionClick(sub.id, index)}
                    className={`p-3 rounded-[2px] cursor-pointer border border-[#3BA2B929] shadow-[0_0_16px_0_#3BA2B91F] ${index === selectedSubsectionIndex ? "bg-teal-50" : "bg-white"}`}
                  >
                    {sub.name}
                  </button>
                ))
              : null}
          </div>
          <div style={scrollableContainer}>
            {questionsLoading ? (
              <div className="flex flex-col gap-y-4 p-4">
                <Skeleton.Button block={true} active={true} />
                <Skeleton.Button block={true} active={true} />
                <Skeleton.Button block={true} active={true} />
                <Skeleton.Button block={true} active={true} />
              </div>
            ) : (
              selectedSubsectionId && <QuestionForm questions={questions} />
            )}
          </div>
        </div>
        <div className="flex justify-between pt-4 pb-8">
          <Button
            type="primary"
            className="text-[13px] w-[140px] border border-[#0087A3] font-bold px-8 py-7 bg-[#0087A31A] text-[#0087A3]"
            onClick={handlePrevious}
            disabled={selectedSubsectionIndex === 0}
          >
            Previous
          </Button>
          <Button
            type="primary"
            className="text-[13px] w-[140px] border-none font-bold px-8 py-7 bg-[#0087A3] text-white"
            onClick={handleNext}
            disabled={
              selectedSubsectionIndex >=
              (selectedSection?.subsection?.length || 0) - 1
            }
          >
            Next
          </Button>
        </div>
      </div>
    </div>
  );
};

export default ViewForm;
